import {
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { resourceCapabilityKeys } from "newui/hooks/useResourceCapability";

import {
	invitePanelMember,
	inviteApprover,
	resolvePanelCoi,
} from "../api/event-grant-api";
import { panelKeys } from "./usePanelScoringQueries";
import type {
	CoiResolutionPayload,
	EventGrantInvitePayload,
	EventGrantResource,
} from "../types/eventGrant.type";

/**
 * React-query mutations for the RBAC event-grant + COI surface (PRO-413).
 * Each mutation invalidates the panel-members query so the list reflects the
 * server-authoritative state immediately (AC6 revoke/close, AC1 invite).
 * Errors propagate unswallowed so callers can surface the server verdict
 * (COI 409, guest-block) — see `parseRbacApiError`.
 */

/** Invite a panel-side principal (panel_member | moderator | observer). */
export const useInvitePanelMember = (
	rfxId: number,
	evalId: number
): UseMutationResult<EventGrantResource, unknown, EventGrantInvitePayload> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: EventGrantInvitePayload) =>
			invitePanelMember(rfxId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: panelKeys.all(rfxId, evalId),
			});
		},
	});
};

/** Invite an approver-side principal (rfx_approver | bid_approver | bid_observer). */
export const useInviteApprover = (
	rfxId: number,
	evalId: number
): UseMutationResult<EventGrantResource, unknown, EventGrantInvitePayload> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: EventGrantInvitePayload) =>
			inviteApprover(rfxId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: panelKeys.all(rfxId, evalId),
			});
		},
	});
};

interface ResolveCoiVariables {
	memberId: number;
	payload: CoiResolutionPayload;
}

/**
 * Resolve a COI (Rule 3). On success invalidates both the panel-members list
 * and any resource-capability caches (a resolution changes the member's live
 * capability, C8) so gating re-reads the server verdict.
 */
export const useResolvePanelCoi = (
	rfxId: number,
	evalId: number
): UseMutationResult<void, unknown, ResolveCoiVariables> => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ memberId, payload }: ResolveCoiVariables) =>
			resolvePanelCoi(rfxId, memberId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: panelKeys.all(rfxId, evalId),
			});
			queryClient.invalidateQueries({
				queryKey: resourceCapabilityKeys.all,
			});
		},
	});
};
